import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  Container, 
  Grid, 
  Heading1, 
  Heading2, 
  Heading3,
  BodyText, 
  BodyLarge, 
  Caption,
  Flex,
  Spacer,
  Badge
} from './ui';
import CinematicHero, { CTA_BUTTON_STYLES } from './ui/CinematicHero';
import { 
  FadeInContainer, 
  FadeInUpContainer, 
  StaggerContainer
} from '../styles/animations';
import {
  MorphingButton,
  BreathingCard,
  CursorAttractor,
  RippleContainer
} from '../styles/microInteractions';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { ReviewsCarouselOption1 } from './ReviewsCarousel';
import { API_BASE_URL } from '../api/tourism';
import AppHeader from './Header';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';
import FlipCard, { FlipCardSkeleton } from './FlipCard';
import { useAutoFlipCards } from '../hooks/useAutoFlipCards';

// Elite Tourism App - Premium Homepage Design
// Following the Elegance Formula: Empathy + Layout + Essentialism + Guidance + Aesthetics + Novelty + Consistency + Engagement

// Premium Main Container with clean white base
const MainContainer = styled.div`
  background: ${getColor('neutral.white')};
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  line-height: 1.6;
  overflow-x: hidden;
  scroll-behavior: smooth;
`;

// Note: Header, Nav, Logo, NavLinks, NavLink styled components are replaced by AppHeader component
// See Header.tsx for navigation styling

// Hero Section now uses shared CinematicHero component from ui/CinematicHero

// Homepage hero spacing — four elements distributed with 15vh top/bottom zones
// Rule 1: Title at top 15%, CTA at bottom 15%
// Rule 2: Pair gaps = 7.5% of 15vh = 1.125vh (title↔subtitle, tagline↔CTA)
const HomeHeroWrapper = styled.div`
  section {
    padding-top: 15vh;
    padding-bottom: 15vh;
    justify-content: stretch;
  }
  /* Content column fills the 70vh middle zone */
  section > div {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
  /* Title → Subtitle: 7.5% of 15vh = 1.125vh */
  section > div > h1 {
    margin-bottom: 1.125vh;
  }
  /* Subtitle: auto margin pushes tagline + CTA to bottom of zone */
  section > div > p:first-of-type {
    font-size: 1.0rem;
    font-weight: 400;
    font-style: italic;
    letter-spacing: 0.08em;
    color: rgba(255, 255, 255, 0.85);
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
    line-height: 1.7;
    opacity: 1;
    max-width: 650px;
    margin-bottom: auto;
  }
  /* Tagline → CTA: 7.5% of 15vh = 1.125vh */
  section > div > p:nth-of-type(2) {
    margin-bottom: 1.125vh;
  }
  /* CTA button: reset default margin-top, spacing handled by tagline gap */
  section > div > button {
    margin-top: 0;
  }

  @media (max-width: 768px) {
    section {
      padding-top: 13vh;
      padding-bottom: 13vh;
    }
    section > div > h1 {
      margin-bottom: 0.975vh;
    }
    section > div > p:first-of-type {
      font-size: 0.9rem;
    }
    section > div > p:nth-of-type(2) {
      margin-bottom: 0.975vh;
    }
  }

  @media (max-width: 480px) {
    section {
      padding-top: 11vh;
      padding-bottom: 11vh;
    }
    section > div > h1 {
      margin-bottom: 0.825vh;
    }
    section > div > p:first-of-type {
      font-size: 0.85rem;
    }
    section > div > p:nth-of-type(2) {
      margin-bottom: 1.5vh;
    }
  }
`;

interface CarouselSlide {
  key: string;
  imageUrl: string;
  name: string;
  description: string;
}

// Styled place description text for carousel hero
const PlaceDescriptionText = styled.div`
  text-align: center;
  max-width: 650px;
  margin: 0 auto;
  transition: opacity 0.6s ease-in-out;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media (max-width: 768px) {
    min-height: 110px;
  }

  @media (max-width: 480px) {
    min-height: 100px;
  }
`;

const PlaceNameLabel = styled.span`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #CAB96E;
  margin-bottom: ${getSpacing('sm')};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    letter-spacing: 0.15em;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    letter-spacing: 0.12em;
  }
`;

const PlaceDescriptionBody = styled.p`
  font-size: 1.0rem;
  letter-spacing: 0.08em;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  line-height: 1.7;
  font-style: italic;
  text-transform: none;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

// Gold accent divider between major sections
const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${getSpacing('xl')};
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    max-width: 120px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      #CAB96E 50%,
      transparent 100%
    );
  }
  
  & > span {
    color: #CAB96E;
    font-size: 0.5rem;
    margin: 0 ${getSpacing('md')};
    opacity: 0.7;
  }
`;

// Elegant Introduction Section - clean white
const IntroSection = styled.section`
  padding: 48px ${getSpacing('lg')} 48px;
  background: ${getColor('neutral.white')};
  position: relative;
  
  @media (max-width: 768px) {
    padding: 24px ${getSpacing('md')} 24px;
  }
  
  @media (max-width: 480px) {
    padding: 16px ${getSpacing('sm')} 16px;
  }
`;

// Container for intro content with better spacing
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IntroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`;

// Separator dot styled component
const SeparatorDot = styled.span`
  color: #CAB96E;
  margin: 0 0.3em;
  font-weight: 500;
  font-size: 3.8rem;
  line-height: 1;
  display: inline-block;
`;

// Story Unfold heading with display medium style
const StoryHeading = styled.div`
  font-size: 2.85rem;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.02em;
  margin-bottom: ${getSpacing('xl')};
  color: ${getColor('primary.900')};
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  font-style: italic;
  text-transform: uppercase;
  word-spacing: 0.1em;
  
  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const StoryLink = styled.a`
  color: #CAB96E;
  cursor: pointer;
  text-decoration: none;
  transition: color ${DesignSystem.motion.duration.fast} ease;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: #CAB96E;
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: #d4c478;
    
    &::after {
      width: 100%;
    }
  }
  
  &:active {
    opacity: 0.7;
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const ExploreHint = styled.div`
  font-size: 0.75rem;
  color: #CAB96E;
  opacity: 0.6;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  margin-top: 12px;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  animation: fadeInHint 1.5s ease-in-out;
  
  @keyframes fadeInHint {
    0% { opacity: 0; transform: translateY(-4px); }
    100% { opacity: 0.6; transform: translateY(0); }
  }
`;

// Premium Destination Cards with Advanced Micro-Interactions
const DestinationCard = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius['3xl']};
  overflow: hidden;
  box-shadow: none;
  border: 1px solid ${getColor('primary.100')};
  position: relative;
  cursor: pointer;
  transition: transform 225ms cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  min-height: 560px;
  will-change: transform;
  
  /* Magnetic attraction effect - no shadow on hover */
  &:hover {
    transform: translateY(-12px) scale(1.02);
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
  }
  
  /* Sophisticated shimmer effect - GPU-accelerated */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: none;
    transform: translateX(-100%);
    z-index: 2;
  }
  
  &:hover::before {
    animation: shimmer ${DesignSystem.motion.duration.slow} ease forwards;
  }
  
  /* Active state with ripple effect */
  &:active {
    transform: translateY(-8px) scale(0.98);
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  
  @media (max-width: 768px) {
    min-height: auto;
  }
`;

const CardImage = styled.div<{ bgImage: string }>`
  height: 360px;
  background: url(${props => props.bgImage}) center/cover;
  position: relative;
  overflow: hidden;
  
  /* Sophisticated gradient overlay */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.3));
  }
  
  @media (max-width: 768px) {
    height: 240px;
  }
  
  @media (max-width: 480px) {
    height: 200px;
  }
`;

const CardContent = styled.div`
  padding: ${getSpacing('xl')};
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('md')};
`;

const CardTitle = styled(Heading3)`
  margin-bottom: 0;
  color: ${getColor('primary.900')};
  flex-shrink: 0;
  font-family: ${DesignSystem.typography.fonts.display};
  font-weight: 500;
  letter-spacing: -0.01em;
`;

const CardDescription = styled(BodyText)`
  color: ${getColor('primary.900')};
  margin-bottom: 0;
  line-height: 1.7;
  flex-grow: 1;
  min-height: 4.25rem;
  display: flex;
  align-items: center;
`;

const CardFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${getSpacing('sm')};
  margin-bottom: 0;
  flex-shrink: 0;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DestinationChip = styled(Badge)`
  font-size: 0.75rem;
`;

// ============================================
// More States Coming Soon Section
// ============================================

const MoreStatesSection = styled.section`
  padding: 48px ${getSpacing('lg')} 48px;
  background: ${getColor('neutral.white')};
  position: relative;
  
  @media (max-width: 768px) {
    padding: 24px ${getSpacing('md')} 24px;
  }
  
  @media (max-width: 480px) {
    padding: 16px ${getSpacing('sm')} 16px;
  }
`;

const MoreStatesContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`;

const StatesBadgesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${getSpacing('xl')};
  margin: ${getSpacing('3xl')} 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: ${getSpacing('lg')};
  }
`;

const StateBadge = styled.div<{ isActive?: boolean }>`
  padding: ${getSpacing('lg')};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  text-align: center;
  transition: all ${DesignSystem.motion.duration.normal} ease;
  cursor: ${props => props.isActive ? 'default' : 'default'};
  
  ${props => props.isActive ? `
    background: linear-gradient(135deg, ${getColor('accent.300')} 0%, ${getColor('accent.400')} 100%);
    border: 2px solid ${getColor('accent.400')};
    box-shadow: 0 8px 24px rgba(212, 184, 150, 0.2);
    
    .badge-title {
      color: white;
      font-weight: 600;
    }
    
    .badge-label {
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.75rem;
      opacity: 1;
    }
  ` : `
    background: ${getColor('neutral.white')};
    border: 2px solid ${getColor('primary.100')};
    opacity: 0.6;
    
    .badge-title {
      color: ${getColor('primary.600')};
      font-weight: 500;
    }
    
    .badge-label {
      color: ${getColor('primary.500')};
      font-size: 0.75rem;
      opacity: 0.8;
    }
  `}
`;

const BadgeTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: ${getSpacing('sm')};
  font-family: ${DesignSystem.typography.fonts.primary};
`;

const BadgeLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-family: ${DesignSystem.typography.fonts.primary};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// Features Section with Sophisticated Layout - Asymmetrical & Bleeding
const FeaturesSection = styled.section`
  padding: 3rem 0;
  background: linear-gradient(
    135deg,
    ${getColor('primary.50')} 0%,
    ${getColor('neutral.white')} 50%,
    ${getColor('primary.50')} 100%
  );
  position: relative;
  overflow: hidden;
  
  // Asymmetrical decorative element
  &::before {
    content: '';
    position: absolute;
    left: -200px;
    bottom: -100px;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, ${getColor('accent.200')} 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0.3;
    z-index: 1;
  }
  
  // Negative space for breathing room
  > * {
    position: relative;
    z-index: 2;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FeatureCard = styled(Card)`
  text-align: center;
  padding: ${getSpacing('2xl')};
  border: none;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 360px;
  height: 100%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-8px);
    box-shadow: ${getShadow('large')};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto ${getSpacing('lg')};
  background: linear-gradient(135deg, ${getColor('accent.400')}, ${getColor('accent.500')});
  border-radius: ${DesignSystem.borderRadius.full};
  display: ${props => props.children ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  box-shadow: ${getShadow('soft')};
`;

// Premium booking component with asymmetrical & dramatic design
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuickBooking = styled(Card)`
  background: linear-gradient(135deg, ${getColor('primary.800')}, ${getColor('primary.900')});
  color: white;
  text-align: center;
  padding: 4rem 3rem;
  margin: ${getSpacing('6xl')} 0;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  // Asymmetrical decorative element
  &::before {
    content: '';
    position: absolute;
    right: -150px;
    top: -150px;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 1;
  }
  
  // Negative space on the left
  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    align-items: center;
    gap: ${getSpacing('4xl')};
  }
`;

// Bold Hero Banner with warm background - Full Bleed
const QuickBookingV2 = styled.div`
  position: relative;
  border-radius: 0;
  box-shadow: none;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: ${getColor('neutral.white')};
  
  /* Content styling */
  & > * {
    position: relative;
    z-index: 2;
    text-align: center;
    padding: 0;
    max-width: 1200px;
  }

  @media (max-width: 768px) {
    padding: ${getSpacing('lg')} 0;
    
    & > * {
      padding: 0;
    }
  }
`;

// ============================================================
// Infinite Scroll Section - Roads & Trails Itineraries Design
// ============================================================

const InfiniteScrollSection = styled.section<{ $warm?: boolean }>`
  position: relative;
  background: ${getColor('neutral.white')};
  padding: 48px 0;
  content-visibility: auto;
  contain-intrinsic-size: auto 600px;
  
  @media (max-width: 768px) {
    padding: 24px 0;
  }
  
  @media (max-width: 480px) {
    padding: 16px 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: ${getSpacing('3xl')};
  padding: 0;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 0 ${getSpacing('md')};
    gap: ${getSpacing('sm')};
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.85rem;
  font-weight: 500;
  color: #CAB96E;
  margin: 0;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: -0.02em;
  font-style: italic;
  line-height: 1.3;
  word-spacing: 0.1em;
  
  @media (max-width: 768px) {
    font-size: clamp(1.6rem, 5vw, 2.4rem);
  }
`;

const ExploreAllLink = styled.a`
  color: ${getColor('primary.900')};
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 400;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${getColor('primary.400')};
  padding: ${getSpacing('xs')} ${getSpacing('sm')};
  transition: all 225ms cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: absolute;
  right: 0;
  
  &:hover {
    opacity: 0.7;
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    position: static;
  }
`;



const MasonryContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: visible;
  padding: 0;
  max-width: 100%;
  margin: 0 auto;
`;

const ScrollableRow = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0;
  margin: 0;
  flex-wrap: nowrap;
  
  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 1199px) {
    gap: 16px;
  }
  
  @media (max-width: 767px) {
    gap: ${getSpacing('md')};
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const ScrollChevron = styled.span<{ direction: 'left' | 'right' }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-right: 2.5px solid currentColor;
  border-bottom: 2.5px solid currentColor;
  transform: ${props => props.direction === 'left' ? 'rotate(135deg)' : 'rotate(-45deg)'};
  transition: transform 0.3s ease;
`;

const ScrollButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${p => p.direction === 'left' ? 'left: -40px;' : 'right: -40px;'}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${getColor('primary.700')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 100;
  padding: 0;
  
  &:hover {
    color: ${getColor('primary.900')};
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    ${p => p.direction === 'left' ? 'left: -32px;' : 'right: -32px;'}
  }
  
  @media (max-width: 480px) {
    ${p => p.direction === 'left' ? 'left: -28px;' : 'right: -28px;'}
  }
`;

// ── FlipCard styled components moved to FlipCard.tsx ──

// Footer styled components for proper state coverage & touch targets
const FooterButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  font-size: 1rem;
  font-family: inherit;
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  min-height: 44px;
  min-width: 44px;
  display: inline-flex;
  align-items: center;
  transition: color ${DesignSystem.motion.duration.fast} ease;
  
  &:hover {
    color: rgba(255, 255, 255, 1);
  }
  
  &:active {
    opacity: 0.7;
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FooterSocialLink = styled.a`
  color: rgba(255, 255, 255, 0.85);
  transition: color ${DesignSystem.motion.duration.fast} ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${getSpacing('sm')};
  min-height: 44px;
  min-width: 44px;
  
  &:hover {
    color: rgba(255, 255, 255, 1);
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

interface HomePageProps {
  onNavigateTo?: (page: 'reviews' | 'home' | 'itinerary' | string, data?: any) => void;
  scrollToDestinations?: boolean;
}

const HomePage: React.FC<HomePageProps> = ({ onNavigateTo, scrollToDestinations = false }) => {
  const [currentPage, setCurrentPage] = useState<string>('home');
  const [homePageItineraries, setHomePageItineraries] = useState<any[]>([]);
  const scrollItinerariesRef = React.useRef<HTMLDivElement>(null);
  const scrollTreksRef = React.useRef<HTMLDivElement>(null);
  const pickJourneySectionRef = useRef<HTMLElement>(null);
  const treksTrailsSectionRef = useRef<HTMLElement>(null);
  const [flippedCards, setFlippedCards] = useState<Set<number | string>>(new Set());
  const [flippedTrekCards, setFlippedTrekCards] = useState<Set<number | string>>(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const isScrollingRef = React.useRef(false);
  const scrollTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Carousel images and descriptions are fetched together from the backend.
  const [carouselSlides, setCarouselSlides] = useState<CarouselSlide[]>([]);
  const carouselImages = useMemo(() => carouselSlides.map(slide => slide.imageUrl), [carouselSlides]);
  const [heroIndex, setHeroIndex] = useState<number>(0);
  
  // Destination card images fetched from backend
  const [destinationCardImages, setDestinationCardImages] = useState<{ [key: string]: string }>({});

  // Header dropdown data via shared hook
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  // Flat trek list for FlipCard rendering (derived from grouped data)
  const flatTreks = useMemo(() => [
    ...trekkingItineraries.ladakh,
    ...trekkingItineraries.kashmir,
    ...trekkingItineraries.himachal,
  ], [trekkingItineraries]);

  // Journey card images for homepage itineraries (same pattern as CuratedExperiences)
  // (moved to module-level JOURNEY_IMAGES / TREK_IMAGES constants for perf)

  // ── Consolidated data fetch: all independent API calls in parallel ──
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Add cache-busting timestamp to force fresh data
        const timestamp = Date.now();
        const [carouselRes, cardsRes, homePageRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/v1/media/carousel?t=${timestamp}`, { cache: 'no-store' }),
          fetch(`${API_BASE_URL}/api/destination-card-images?t=${timestamp}`, { cache: 'no-store' }),
          fetch(`${API_BASE_URL}/api/v1/itineraries/home-page?t=${timestamp}`, { cache: 'no-store' }),
        ]);

        // Carousel images
        if (carouselRes.ok) {
          const data = await carouselRes.json();
          const slides: CarouselSlide[] = (data.items || []).map((item: {
            key: string;
            image_url: string;
            name: string;
            description: string;
          }) => ({
            key: item.key,
            imageUrl: `${API_BASE_URL}${new URL(item.image_url, API_BASE_URL).pathname}`,
            name: item.name,
            description: item.description,
          }));
          setCarouselSlides(slides);
          setHeroIndex(0);
        } else {
          console.error(`Failed to load homepage carousel images: HTTP ${carouselRes.status}`);
        }

        // Destination card images
        if (cardsRes.ok) {
          const data = await cardsRes.json();
          const cards: Record<string, string> = data.destination_cards || {};
          setDestinationCardImages(cards);
        }

        // Home page itineraries
        if (homePageRes.ok) {
          const data = await homePageRes.json();
          const items = data.itineraries || data || [];
          for (let i = items.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [items[i], items[j]] = [items[j], items[i]]; }
          setHomePageItineraries(items);
          console.log('[HomePage] Fetched fresh home-page itineraries:', items[0]?.cardImage);
        }
      } catch (error) {
        console.error('Error fetching homepage data:', error);
      }
    };

    fetchAllData();
  }, []);

  const nextHero = useCallback(() => {
    if (carouselImages.length === 0) return;
    setHeroIndex(i => (i + 1) % carouselImages.length);
  }, [carouselImages.length]);
  const prevHero = useCallback(() => {
    if (carouselImages.length === 0) return;
    setHeroIndex(i => (i - 1 + carouselImages.length) % carouselImages.length);
  }, [carouselImages.length]);

  // Keyboard navigation for hero images
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        prevHero();
      } else if (e.key === 'ArrowRight') {
        nextHero();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextHero, prevHero]);

  // Auto-advance images every 7.5 seconds
  useEffect(() => {
    if (carouselImages.length === 0) return;
    const autoSlideInterval = setInterval(() => {
      setHeroIndex(i => (i + 1) % carouselImages.length);
    }, 7500);
    return () => clearInterval(autoSlideInterval);
  }, [carouselImages.length]);

  // Scroll to destinations when navigated from header
  useEffect(() => {
    if (scrollToDestinations) {
      setTimeout(() => {
        const destinationsSection = document.getElementById('destinations');
        if (destinationsSection) {
          destinationsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [scrollToDestinations]);

  // Mobile detection for flip card tap behavior
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile/tablet, auto-scroll to "PICK YOUR JOURNEY" section so users start from itineraries
  useEffect(() => {
    if (scrollToDestinations) return; // skip if already scrolling to destinations
    const isMobileOrTablet = window.innerWidth <= 1024;
    if (isMobileOrTablet) {
      const timer = setTimeout(() => {
        const section = document.getElementById('pick-journey-divider');
        if (section) {
          const y = section.getBoundingClientRect().top + window.scrollY - 60;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-flip even-indexed cards when sections scroll into view
  const journeyCardIds = useMemo<(number | string)[]>(() => homePageItineraries.map((_: any, i: number) => i), [homePageItineraries]);
  const trekCardIds = useMemo<(number | string)[]>(() => flatTreks.map((_: any, i: number) => i), [flatTreks]);
  useAutoFlipCards(pickJourneySectionRef, journeyCardIds, setFlippedCards);
  useAutoFlipCards(treksTrailsSectionRef, trekCardIds, setFlippedTrekCards);

  // Toggle flip on mobile tap for journey cards
  const handleFlipCardTap = useCallback((idx: number | string) => {
    if (!isMobile || isScrollingRef.current) return;
    setFlippedCards(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        // Only allow one card flipped at a time on mobile
        next.clear();
        next.add(idx);
      }
      return next;
    });
  }, [isMobile]);

  // Toggle flip on mobile tap for trek cards
  const handleTrekFlipCardTap = useCallback((idx: number | string) => {
    if (!isMobile || isScrollingRef.current) return;
    setFlippedTrekCards(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        // Only allow one card flipped at a time on mobile
        next.clear();
        next.add(idx);
      }
      return next;
    });
  }, [isMobile]);

  // Scroll guard: prevent accidental flips while scrolling journey carousel
  useEffect(() => {
    const scrollEl = scrollItinerariesRef.current;
    if (!scrollEl) return;
    const handleScroll = () => {
      isScrollingRef.current = true;
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  // Scroll guard: prevent accidental flips while scrolling trek carousel
  useEffect(() => {
    const scrollEl = scrollTreksRef.current;
    if (!scrollEl) return;
    const handleScroll = () => {
      isScrollingRef.current = true;
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);
  
  const destinations = [
    {
      id: 1,
      title: "Ladakh",
      description: "Himalayan peaks, high-altitude lakes, ancient monasteries, and mountain passes in India's breathtaking destination.",
      category: "Adventure & Culture",
      duration: "5-10 days",
      image: destinationCardImages['ladakh'] || `${API_BASE_URL}/api/images/destination_cards/ladakh_card.jpg`,
      destinations: ["Leh", "Nubra Valley", "Pangong Lake", "Khardung La"],
      state: "ladakh"
    },
    {
      id: 2,
      title: "Kashmir",
      description: "Discover the breathtaking valleys, pristine lakes, and snow-capped peaks of India's northernmost paradise.",
      category: "Nature & Serenity",
      duration: "6-12 days",
      image: destinationCardImages['jammu'] || `${API_BASE_URL}/api/images/destination_cards/jammu_kashmir_card.jpg`,
      destinations: ["Srinagar", "Gulmarg", "Pahalgam", "Sonamarg"],
      state: "kashmir"
    },
    {
      id: 3,
      title: "Himachal Pradesh",
      description: "Explore the scenic hill stations, adventure sports, and spiritual retreats in the land of gods and mountains.",
      category: "Mountains & Adventure",
      duration: "4-8 days",
      image: destinationCardImages['himachal'] || `${API_BASE_URL}/api/images/destination_cards/himachal_card.jpg`,
      destinations: ["Manali", "Shimla", "Dharamshala", "Kasol"],
      state: "himachal"
    }
  ];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const features = [
    {
      icon: "",
      title: "Expert Guides",
      description: "Local expertise with deep knowledge of culture and terrain"
    },
    {
      icon: "",
      title: "Premium Safety",
      description: "Comprehensive safety protocols and emergency support"
    },
    {
      icon: "",
      title: "Luxury Comfort",
      description: "Carefully selected accommodations and premium amenities"
    },
    {
      icon: "",
      title: "Unique Experiences",
      description: "Exclusive access to hidden gems and cultural immersion"
    }
  ];

  const handleDestinationSelect = (destination: any) => {
    if (onNavigateTo) {
      onNavigateTo('destination-page', { destination });
    }
    console.log('Selected destination:', destination);
  };

  const handleNavigation = (page: string, data?: any) => {
    setCurrentPage(page);
    if (onNavigateTo) {
      onNavigateTo(page, data);
    }
  };

  const handleAdventureTypeSelect = (tourType: 'car' | 'bike' | 'trek') => {
    setCurrentPage('curated-experiences');
    onNavigateTo?.('curated-experiences', {
      destination: null,
      fromHomepage: true,
      tourType,
    });
  };

  return (
    <MainContainer>
      <AppHeader 
        onNavigateTo={handleNavigation} 
        currentPage={currentPage} 
        transparent 
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      <FadeInContainer>
        <HomeHeroWrapper>
        <CinematicHero
          id="home"
          images={carouselImages}
          activeIndex={heroIndex}
          height="100vh"
          mobileHeight="100vh"
          brightness={1.05}
          contrast={1.1}
          title={<>Discover the <span>Himalayas</span></>}
          subtitle="Embark on extraordinary journeys through pristine landscapes, ancient cultures, and breathtaking adventures in India's crown jewel."
          tagline={(() => {
            const place = carouselSlides[heroIndex];
            return place ? (
              <PlaceDescriptionText>
                <PlaceNameLabel>{place.name}</PlaceNameLabel>
                <PlaceDescriptionBody>{place.description}</PlaceDescriptionBody>
              </PlaceDescriptionText>
            ) : null;
          })()}
          ctaText="Begin Your Journey"
          onCtaClick={() => {
            if (onNavigateTo) {
              onNavigateTo('curated-experiences');
            }
          }}
          showNavArrows
          onPrev={prevHero}
          onNext={nextHero}
          prevArrow={<ScrollChevron direction="left" />}
          nextArrow={<ScrollChevron direction="right" />}
          prevAriaLabel={`Previous image (${heroIndex === 0 ? carouselImages.length : heroIndex} of ${carouselImages.length})`}
          nextAriaLabel={`Next image (${heroIndex === carouselImages.length - 1 ? 1 : heroIndex + 2} of ${carouselImages.length})`}
        />
        </HomeHeroWrapper>
      </FadeInContainer>

      <FadeInUpContainer delay={0.2}>
        <IntroSection>
          <Container maxWidth="lg">
            <div style={{ textAlign: 'center' }}>
              <Caption style={{ fontStyle: 'italic', letterSpacing: '0.08em', fontWeight: 400, fontSize: '1.5rem', fontFamily: "'Poppins', 'Montserrat', 'Futura', sans-serif", textTransform: 'uppercase' as const }}>CHOOSE YOUR ADVENTURE STYLE</Caption>
              <Spacer size="xl" />
              <StoryHeading>
                <StoryLink onClick={() => handleAdventureTypeSelect('car')}>
                  Roads
                </StoryLink>
                <SeparatorDot>·</SeparatorDot>
                <StoryLink onClick={() => handleAdventureTypeSelect('bike')}>
                  Rides
                </StoryLink>
                <SeparatorDot>·</SeparatorDot>
                <StoryLink onClick={() => handleAdventureTypeSelect('trek')}>
                  Treks
                </StoryLink>
              </StoryHeading>
              <ExploreHint>↑ tap to explore ↑</ExploreHint>
              <Spacer size="xl" />
              <BodyLarge style={{ fontStyle: 'italic', letterSpacing: '0.08em', lineHeight: 1.7, maxWidth: '650px', margin: '0 auto' }}>
                From comfortable road journeys and thrilling bike expeditions to immersive trekking adventures - explore the Himalayas the way that speaks to your spirit.
              </BodyLarge>
            </div>
          </Container>
        </IntroSection>
      </FadeInUpContainer>

      <section id="destinations" style={{ padding: '48px 0' }}>
        <Container maxWidth="xl">
          <StaggerContainer>
            <Grid 
              cols={1} 
              gap="xl"
              responsiveCols={{ md: 2, lg: 3 }}
              style={{ gap: getSpacing('2xl') }}
            >
              {destinations.map((destination, index) => (
                <BreathingCard key={destination.id} delay={index * 0.2}>
                  <CursorAttractor attractionStrength={8}>
                    <DestinationCard 
                      onClick={() => handleDestinationSelect(destination)}
                    >
                      <CardImage bgImage={destination.image}>
                      </CardImage>
                      <CardContent>
                        <CardTitle>{destination.title}</CardTitle>
                    <CardDescription>
                      {destination.description}
                    </CardDescription>
                    <CursorAttractor attractionStrength={15}>
                      <RippleContainer>
                        <MorphingButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDestinationSelect(destination);
                          }}
                          style={{
                            ...CTA_BUTTON_STYLES,
                            width: '100%',
                            padding: '14px 32px',
                            fontSize: '0.75rem',
                          }}
                        >
                          Plan Your Escape →
                        </MorphingButton>
                      </RippleContainer>
                    </CursorAttractor>
                  </CardContent>
                    </DestinationCard>
                  </CursorAttractor>
                </BreathingCard>
              ))}
            </Grid>
          </StaggerContainer>
        </Container>
      </section>

      <SectionDivider><span>◆</span></SectionDivider>

      <FadeInUpContainer delay={0.3}>
        <MoreStatesSection id="more-states">
          <Container maxWidth="lg">
            <MoreStatesContent>
              <Caption style={{ fontStyle: 'italic', letterSpacing: '0.08em', fontWeight: 400, fontSize: '1.5rem', fontFamily: "'Poppins', 'Montserrat', 'Futura', sans-serif", textTransform: 'uppercase' as const }}>COMING TO MORE STATES</Caption>
              <Spacer size="xl" />
              <BodyLarge style={{ color: getColor('primary.900'), fontStyle: 'italic', letterSpacing: '0.08em', lineHeight: 1.7, maxWidth: '650px', margin: '0 auto' }}>
                We're starting with Ladakh, Kashmir, and Himachal Pradesh. Over the coming months and quarters, we'll add new regions and open up diverse travel possibilities.
              </BodyLarge>
            </MoreStatesContent>
          </Container>
        </MoreStatesSection>
      </FadeInUpContainer>

      <SectionDivider id="pick-journey-divider"><span>◆</span></SectionDivider>

      {/* Handcrafted Itineraries Section - Single Row Horizontal Scroll */}
      <InfiniteScrollSection id="pick-your-journey" ref={pickJourneySectionRef}>
        <Container maxWidth="xl">
          <SectionHeader>
            <SectionTitle>PICK YOUR JOURNEY</SectionTitle>
            <ExploreAllLink 
              onClick={(e) => {
                e.preventDefault();
                onNavigateTo?.('curated-experiences', { 
                  destination: null,
                  fromHomepage: true 
                });
              }}
            >
              See all →
            </ExploreAllLink>
          </SectionHeader>

          <MasonryContainer>
            <ScrollButton 
              direction="left" 
              onClick={() => {
                if (scrollItinerariesRef.current) {
                  scrollItinerariesRef.current.scrollBy({ left: -300, behavior: 'smooth' });
                }
              }}
              title="Scroll left"
            >
              <ScrollChevron direction="left" />
            </ScrollButton>
            
            <ScrollableRow ref={scrollItinerariesRef}>
              {homePageItineraries.length === 0 && [1,2,3,4].map(i => <FlipCardSkeleton key={`skel-j-${i}`} />)}
              {homePageItineraries.map((itinerary, idx) => (
                <FlipCard
                  key={itinerary.id || itinerary.name || `itinerary-${idx}`}
                  item={itinerary}
                  variant="journey"
                  index={idx}
                  isFlipped={flippedCards.has(idx)}
                  isMobile={isMobile}
                  onFlip={handleFlipCardTap}
                  onExplore={(item) => {
                    onNavigateTo && onNavigateTo('detailed-itinerary', {
                      state: item.state?.[0] || 'Ladakh',
                      itineraryId: item.id,
                      fromHomepage: true
                    });
                  }}
                  onFrontClick={(item) => {
                    onNavigateTo && onNavigateTo('detailed-itinerary', {
                      state: item.state?.[0] || 'Ladakh',
                      itineraryId: item.id,
                      fromHomepage: true
                    });
                  }}
                />
              ))}
            </ScrollableRow>

            <ScrollButton 
              direction="right" 
              onClick={() => {
                if (scrollItinerariesRef.current) {
                  scrollItinerariesRef.current.scrollBy({ left: 300, behavior: 'smooth' });
                }
              }}
              title="Scroll right"
            >
              <ScrollChevron direction="right" />
            </ScrollButton>
          </MasonryContainer>
        </Container>
      </InfiniteScrollSection>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* Treks and Trails Section - Same flip card layout as PICK YOUR JOURNEY */}
      <InfiniteScrollSection $warm ref={treksTrailsSectionRef}>
        <Container maxWidth="xl">
          <SectionHeader>
            <SectionTitle>TREKS & TRAILS</SectionTitle>
            <ExploreAllLink 
              onClick={(e) => {
                e.preventDefault();
                onNavigateTo?.('curated-experiences', { 
                  destination: null,
                  fromHomepage: true 
                });
              }}
            >
              See all →
            </ExploreAllLink>
          </SectionHeader>

          <MasonryContainer>
            <ScrollButton 
              direction="left" 
              onClick={() => {
                if (scrollTreksRef.current) {
                  scrollTreksRef.current.scrollBy({ left: -300, behavior: 'smooth' });
                }
              }}
              title="Scroll left"
            >
              <ScrollChevron direction="left" />
            </ScrollButton>
            
            <ScrollableRow ref={scrollTreksRef}>
              {flatTreks.length === 0 && [1,2,3,4].map(i => <FlipCardSkeleton key={`skel-t-${i}`} />)}
              {flatTreks.map((trek: any, idx: number) => (
                <FlipCard
                  key={trek.id || trek.name || `trek-${idx}`}
                  item={trek}
                  variant="trek"
                  index={idx}
                  isFlipped={flippedTrekCards.has(idx)}
                  isMobile={isMobile}
                  onFlip={handleTrekFlipCardTap}
                  onExplore={(item) => {
                    const trekState = item.state?.[0]
                      || (item.region?.toLowerCase().includes('kashmir') ? 'Kashmir' : undefined)
                      || (item.region?.toLowerCase().includes('himachal') ? 'Himachal' : undefined)
                      || 'Ladakh';
                    onNavigateTo && onNavigateTo('trek-detail', {
                      state: trekState,
                      itineraryId: item.id,
                      fromHomepage: true
                    });
                  }}
                  onFrontClick={(item) => {
                    const trekState = item.state?.[0]
                      || (item.region?.toLowerCase().includes('kashmir') ? 'Kashmir' : undefined)
                      || (item.region?.toLowerCase().includes('himachal') ? 'Himachal' : undefined)
                      || 'Ladakh';
                    onNavigateTo && onNavigateTo('trek-detail', {
                      state: trekState,
                      itineraryId: item.id,
                      fromHomepage: true
                    });
                  }}
                />
              ))}
            </ScrollableRow>

            <ScrollButton 
              direction="right" 
              onClick={() => {
                if (scrollTreksRef.current) {
                  scrollTreksRef.current.scrollBy({ left: 300, behavior: 'smooth' });
                }
              }}
              title="Scroll right"
            >
              <ScrollChevron direction="right" />
            </ScrollButton>
          </MasonryContainer>
        </Container>
      </InfiniteScrollSection>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* Customer Reviews Section - Choose one option below */}
      <section data-section="reviews">
        {/* OPTION 1: Clean Grid with Arrow Navigation (RECOMMENDED) */}
        <ReviewsCarouselOption1 />
        
        {/* OPTION 2: Sliding Tray with Smooth Animation */}
        {/* <ReviewsCarouselOption2 /> */}
        
        {/* OPTION 3: Tab-Based Navigation */}
        {/* <ReviewsCarouselOption3 /> */}
      </section>

      <SectionDivider><span>◆</span></SectionDivider>

      <QuickBookingV2 style={{ marginTop: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: getSpacing('sm'), padding: `${getSpacing('lg')} ${getSpacing('xl')}`, maxWidth: '800px' }}>
          <Heading2 style={{ 
            color: getColor('primary.900'), 
            marginBottom: getSpacing('sm'),
            fontSize: '1.5rem',
            fontWeight: 400,
            fontStyle: 'italic',
            letterSpacing: '0.08em',
            lineHeight: 1.25,
            textTransform: 'uppercase' as const,
            fontFamily: "'Poppins', 'Montserrat', 'Futura', sans-serif"
          }}>
            Your Himalayan Adventure Awaits
          </Heading2>
          <BodyLarge style={{ 
            color: getColor('primary.900'), 
            marginBottom: getSpacing('md'),
            maxWidth: '600px',
            fontStyle: 'italic',
            letterSpacing: '0.08em',
            lineHeight: 1.7,
            fontWeight: 400
          }}>
            Chat with our travel expert to plan your perfect Himalayan getaway - from routes to stays, we've got you covered.
          </BodyLarge>
        </div>
        </QuickBookingV2>

      <footer style={{ 
        background: '#3D3721', 
        color: 'white', 
        padding: `${getSpacing('2xl')} 0`,
        textAlign: 'center' as const
      }}>
        <Container maxWidth="lg">
          <BodyText style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Join thousands of travelers who have discovered the magic of the Himalayas with us.
          </BodyText>
          <Spacer size="lg" />
          <Flex justify="center" gap="lg" wrap>
            <FooterButton 
              onClick={() => onNavigateTo?.('about')}
            >
              About
            </FooterButton>
            <FooterButton 
              onClick={() => {
                const destinationsSection = document.getElementById('destinations');
                if (destinationsSection) {
                  destinationsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Gallery
            </FooterButton>
            <FooterButton 
              onClick={() => onNavigateTo?.('reviews')}
            >
              Reviews
            </FooterButton>
            <FooterButton 
              onClick={() => onNavigateTo?.('contact')}
            >
              Contact
            </FooterButton>
          </Flex>
          <Spacer size="lg" />
          <Flex justify="center" gap="md" style={{ marginBottom: getSpacing('md') }}>
            <FooterSocialLink href="https://www.instagram.com/himalayan__rovers/" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </FooterSocialLink>
            {/* <FooterSocialLink href="https://www.facebook.com/himalayanrovers" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
            </FooterSocialLink> */}
          </Flex>
          <Caption style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            © 2026 Himalayan Rovers. All rights reserved.
          </Caption>
        </Container>
      </footer>
      
      {/* Destination Detail Modal - Moved to full page (DestinationPage.tsx) */}
      {/* 
      <DestinationDetail 
        destination={selectedDestination}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        onNavigateToItinerary={(state: string) => {
          if (onNavigateTo) {
            onNavigateTo('itinerary', { state });
          }
        }}
      />
      */}
    </MainContainer>
  );
};

export default HomePage;